import {
  DB_PERSISTED_FEATURE_FLAGS_KEYS,
  DB_PERSISTED_KEYS_SAFE_BEFORE_INITIALIZATION,
  DbPersistedFeatureFlags,
  FeatureFlag,
  FeatureFlagConfig,
} from './featureFlag-types';

export interface Storage {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
}

export interface FeatureFlagStore {
  getFeatureFlags(): Partial<Record<FeatureFlag, boolean>>;
  getFeatureFlag(featureFlag: FeatureFlag): boolean | undefined;
  setFeatureFlag(featureFlag: FeatureFlag, value: boolean): void;
  clearFeatureFlags(): void;
  removeFeatureFlag(featureFlag: FeatureFlag): void;
  getFeatureFlagsConfig(): Partial<Record<FeatureFlagConfig, any>>;
  setFeatureFlagConfig(featureFlag: FeatureFlag, config: any): void;
  getFeatureFlagConfig(featureFlag: FeatureFlag): any;
  setDbPersistedFeatureFlag<K extends keyof DbPersistedFeatureFlags>(
    featureFlag: K,
    value: DbPersistedFeatureFlags[K],
  ): void;
  getDbPersistedFeatureFlags(): Partial<DbPersistedFeatureFlags>;
  setDbPersistedFeatureFlags(featureFlags: Partial<DbPersistedFeatureFlags>): void;
  isKeyInDbPersistedFeatureFlags(key: string): boolean;
  isDbPersistedKeySafeBeforeInitialization(key: string): boolean;
  setDbPersistedConfigStoreInitialized(initialized: boolean): void;
  isDbPersistedConfigStoreInitialized(): boolean;
}

export function createFeatureFlagStore(storage: Storage): FeatureFlagStore {
  const featureFlagStorageTopKey = 'neptune-store-feature-flags';
  const featureFlagsKey = 'featureFlags';
  const featureFlagsConfigKey = 'featureFlagsConfig';

  let dbPersistedConfigStoreInitialized = false;

  function getFeatureFlags(): Partial<Record<FeatureFlag, boolean>> {
    const storedValue = storage.getItem(featureFlagStorageTopKey);

    if (!storedValue) {
      return {};
    }

    try {
      const parsedValue = JSON.parse(storedValue);
      return parsedValue[featureFlagsKey] || {};
    } catch {
      return {};
    }
  }

  function getFeatureFlag(featureFlag: FeatureFlag): boolean | undefined {
    const featureFlags = getFeatureFlags();
    return featureFlags[featureFlag];
  }

  function setFeatureFlags(featureFlags?: Partial<Record<FeatureFlag, boolean>>): void {
    const storedValue = storage.getItem(featureFlagStorageTopKey);

    if (!storedValue) {
      storage.setItem(
        featureFlagStorageTopKey,
        JSON.stringify({ [featureFlagsKey]: featureFlags }),
      );

      return;
    }

    try {
      const parsedValue = JSON.parse(storedValue);

      storage.setItem(
        featureFlagStorageTopKey,
        JSON.stringify({
          ...parsedValue,
          [featureFlagsKey]: featureFlags,
        }),
      );
    } catch {
      // eslint-disable-next-line no-console
      console.error('Failed to set feature flags');
    }
  }

  function setFeatureFlag(featureFlag: FeatureFlag, value: boolean): void {
    const featureFlags = getFeatureFlags();

    setFeatureFlags({
      ...featureFlags,
      [featureFlag]: value,
    });
  }

  function clearFeatureFlags(): void {
    setFeatureFlags({});
  }

  function removeFeatureFlag(featureFlag: FeatureFlag): void {
    const featureFlags = getFeatureFlags();
    delete featureFlags[featureFlag];
    setFeatureFlags(featureFlags);
  }

  function getFeatureFlagsConfig(): Partial<Record<FeatureFlagConfig, any>> {
    const storedValue = storage.getItem(featureFlagStorageTopKey);

    if (!storedValue) {
      return {};
    }

    try {
      const parsedValue = JSON.parse(storedValue);
      return parsedValue[featureFlagsConfigKey] || {};
    } catch {
      return {};
    }
  }

  function setFeatureFlagsConfig(
    featureFlagsConfig?: Partial<Record<FeatureFlagConfig, any>>,
  ): void {
    const storedValue = storage.getItem(featureFlagStorageTopKey);

    if (!storedValue) {
      storage.setItem(
        featureFlagStorageTopKey,
        JSON.stringify({ [featureFlagsConfigKey]: featureFlagsConfig }),
      );

      return;
    }

    try {
      const parsedValue = JSON.parse(storedValue);

      storage.setItem(
        featureFlagStorageTopKey,
        JSON.stringify({
          ...parsedValue,
          [featureFlagsConfigKey]: featureFlagsConfig,
        }),
      );
    } catch {
      // eslint-disable-next-line no-console
      console.error('Failed to set feature flags config');
    }
  }

  function setFeatureFlagConfig(featureFlag: FeatureFlag, config: any): void {
    const featureFlagsConfig = getFeatureFlagsConfig();
    const configKey: FeatureFlagConfig = `${featureFlag}Config`;

    setFeatureFlagsConfig({
      ...featureFlagsConfig,
      [configKey]: typeof config === 'string' ? config : JSON.stringify(config),
    });
  }

  function getFeatureFlagConfig(featureFlag: FeatureFlag): any {
    const featureFlagsConfig = getFeatureFlagsConfig();
    const configKey = `${featureFlag}Config` as FeatureFlagConfig;
    const configValue = featureFlagsConfig[configKey];

    if (configValue === undefined) {
      return undefined;
    }

    try {
      return JSON.parse(configValue);
    } catch {
      return configValue;
    }
  }

  function setDbPersistedFeatureFlag<K extends keyof DbPersistedFeatureFlags>(
    featureFlag: K,
    value: DbPersistedFeatureFlags[K],
  ): void {
    setFeatureFlag(featureFlag, value);
  }

  function getDbPersistedFeatureFlags(): Partial<DbPersistedFeatureFlags> {
    const allFeatureFlags = getFeatureFlags();

    const dbPersistedFeatureFlags: Partial<DbPersistedFeatureFlags> = {};

    for (const key of DB_PERSISTED_FEATURE_FLAGS_KEYS) {
      if (key in allFeatureFlags) {
        dbPersistedFeatureFlags[key] = allFeatureFlags[key];
      }
    }

    return dbPersistedFeatureFlags;
  }

  function setDbPersistedFeatureFlags(featureFlags: Partial<DbPersistedFeatureFlags>): void {
    const currentFeatureFlags = getFeatureFlags();
    const updatedFeatureFlags = {
      ...currentFeatureFlags,
      ...featureFlags,
    };
    setFeatureFlags(updatedFeatureFlags);
  }

  function isKeyInDbPersistedFeatureFlags(key: string): boolean {
    const listAsStrings: readonly string[] = DB_PERSISTED_FEATURE_FLAGS_KEYS;
    return listAsStrings.includes(key);
  }

  function isDbPersistedKeySafeBeforeInitialization(key: string): boolean {
    if (!isKeyInDbPersistedFeatureFlags(key)) {
      return true;
    }

    return DB_PERSISTED_KEYS_SAFE_BEFORE_INITIALIZATION.includes(
      key as keyof DbPersistedFeatureFlags,
    );
  }

  function setDbPersistedConfigStoreInitialized(initialized: boolean): void {
    dbPersistedConfigStoreInitialized = initialized;
  }

  function isDbPersistedConfigStoreInitialized(): boolean {
    return dbPersistedConfigStoreInitialized;
  }

  return {
    getFeatureFlags,
    getFeatureFlag,
    setFeatureFlag,
    clearFeatureFlags,
    removeFeatureFlag,
    getFeatureFlagsConfig,
    setFeatureFlagConfig,
    getFeatureFlagConfig,
    setDbPersistedFeatureFlag,
    getDbPersistedFeatureFlags,
    setDbPersistedFeatureFlags,
    isKeyInDbPersistedFeatureFlags,
    isDbPersistedKeySafeBeforeInitialization,
    setDbPersistedConfigStoreInitialized,
    isDbPersistedConfigStoreInitialized,
  };
}

// Create and export a default instance using window.localStorage
export const featureFlagStore = createFeatureFlagStore(localStorage);
